import React from "react";
import { Crown, Landmark, Trophy, Ticket, Dog, Shirt, UtensilsCrossed } from "lucide-react";
import WrestlerPortrait from "../WrestlerPortrait";
import { ClassIcon } from "../ClassIcon";
import { SlotShell } from "../roster/RosterUI";

export function ProfileStat({ label, color, icon: Icon, testId, children, extra, delay = 0 }) {
  return (
    <div data-testid={testId} className="pf-stat wr-rise group relative overflow-hidden rounded-xl p-[2px]"
         style={{ background: `linear-gradient(140deg, ${color}, #5B3BFF55, ${color}66)`, animationDelay: `${delay}ms` }}>
      <div className="relative h-full rounded-[10px] bg-[#0A051A]/90 backdrop-blur px-3.5 py-3">
        <div className="pointer-events-none absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-30 transition-opacity duration-300 group-hover:opacity-60" style={{ background: color }} />
        <Icon size={46} strokeWidth={1.6} className="pf-watermark pointer-events-none absolute -right-1 -bottom-2 opacity-15 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" style={{ color }} />
        <div className="relative flex items-center gap-1.5">
          <Icon size={15} strokeWidth={2.6} style={{ color, filter: `drop-shadow(0 0 5px ${color})` }} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#C9BDF0]">{label}</span>
        </div>
        <div className="relative mt-1.5 mb-2 h-[2px] rounded-full" style={{ background: `linear-gradient(90deg, ${color}, ${color}00)`, boxShadow: `0 0 6px ${color}66` }} />
        <div className="relative">{children}</div>
        {extra}
      </div>
    </div>
  );
}

export function AffilChip({ label, color, onClick, testId, title, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#C9BDF0] mb-1.5">{label}</div>
      <Tag data-testid={testId} onClick={onClick} title={title}
           className={`group inline-flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-xl border-2 bg-[#0A051A]/75 backdrop-blur font-graffiti uppercase text-lg leading-none transition-[transform,box-shadow,background-color] duration-200 ${onClick ? "hover:-translate-y-0.5 active:scale-95" : ""}`}
           style={{ borderColor: `${color}AA`, color, boxShadow: `0 0 14px ${color}33`, textShadow: `0 0 10px ${color}88` }}>
        {children}
      </Tag>
    </div>
  );
}

function GearThumb({ item }) {
  if (!item) {
    return <div className="aspect-square rounded-lg border border-dashed border-[#4A3470] bg-[#0A051A]/60 flex items-center justify-center text-[9px] text-[#3D2A5A] font-mono">—</div>;
  }
  return (
    <div className="group/g relative aspect-square rounded-lg p-[1.5px]" title={`${item.name}${item.level ? ` · L${item.level}` : ""}`}
         style={{ background: item.level >= 3 ? "linear-gradient(140deg,#FFD700,#FF9500)" : "linear-gradient(140deg,#5B3BFF,#00F0FF)" }}>
      <div className="w-full h-full rounded-[7px] overflow-hidden bg-[#0A051A]">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-200 group-hover/g:scale-110"
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
      {item.level > 1 && (
        <div className="absolute -top-1 -right-1 px-1 rounded font-num text-[10px] leading-tight bg-[#0A051A] border"
             style={{ color: item.level >= 3 ? "#FFD700" : "#39FF14", borderColor: item.level >= 3 ? "#FFD70088" : "#39FF1488" }}>
          L{item.level}
        </div>
      )}
    </div>
  );
}

function MiniHeading({ icon: Icon, color, children }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-1.5">
        <Icon size={14} strokeWidth={2.6} className="shrink-0" style={{ color, filter: `drop-shadow(0 0 5px ${color})` }} />
        <span className="font-graffiti uppercase text-sm leading-none" style={{ color, textShadow: `0 0 10px ${color}99, 1px 1px 0 #000` }}>{children}</span>
      </div>
      <div className="mt-1 h-[2px] rounded-full" style={{ background: `linear-gradient(90deg, ${color}, ${color} 35%, transparent)`, boxShadow: `0 0 6px ${color}88` }} />
    </div>
  );
}

function GearRow({ icon: Icon, label, items, color }) {
  return (
    <div>
      <MiniHeading icon={Icon} color={color}>{label}</MiniHeading>
      <div className="grid grid-cols-3 gap-1.5">{items.map((it, i) => <GearThumb key={i} item={it} />)}</div>
    </div>
  );
}

function Accomp({ icon: Icon, label, value, color, testId }) {
  return (
    <div data-testid={testId} className="group/a flex items-center gap-1.5 rounded-md px-2 py-1.5 bg-[#0A051A]/80 border transition-colors hover:bg-[#1C0D40]" style={{ borderColor: `${color}55` }}>
      <Icon size={14} strokeWidth={2.6} className="shrink-0 transition-transform group-hover/a:scale-125" style={{ color, filter: `drop-shadow(0 0 4px ${color})` }} />
      <span className="text-[11px] font-mono font-bold text-[#E2DAFF] flex-1">{label}</span>
      <span className="font-num text-lg leading-none" style={{ color, textShadow: `0 0 8px ${color}88` }}>{value}</span>
    </div>
  );
}

export function RosterShowcaseCard({ w, delay, divisions, labels, short, colors }) {
  const cls = w.class_info || {};
  const c = cls.color || "#FF007F";
  return (
    <SlotShell testId={`profile-wrestler-${w.wrestler_id}`} delay={delay} colors={[c, "#5B3BFF", "#00F0FF"]}>
      <div className="relative rounded-xl p-[3px] mb-2" style={{ background: `linear-gradient(140deg, ${c}, #5B3BFF, #00F0FF)` }}>
        <div className="relative aspect-square rounded-[9px] overflow-hidden bg-[#0A051A]">
          <WrestlerPortrait w={w} showCrown />
          <div className="absolute top-1.5 left-1.5 px-1.5 py-1 rounded-lg text-[9px] font-mono font-bold flex items-center gap-1 bg-[#0A051A]/85 backdrop-blur border-2"
               style={{ borderColor: c, color: c, boxShadow: `0 0 10px ${c}66` }}>
            <ClassIcon classId={cls.id} color={c} size={12} testId={`profile-class-icon-${w.wrestler_id}`} />
            <span className="uppercase tracking-wider hidden sm:inline">{cls.name}</span>
          </div>
          <div data-testid={`profile-wrestler-level-${w.wrestler_id}`} className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-lg bg-[#0A051A]/85 backdrop-blur border-2 border-[#00F0FF] font-num text-base leading-tight text-[#00F0FF]"
               style={{ boxShadow: "0 0 10px #00F0FF66", textShadow: "0 0 8px #00F0FF" }}>
            LV {w.level}
          </div>
          {w.current_title && (
            <div className="absolute inset-x-0 bottom-0 px-2 pt-5 pb-1.5 flex items-center gap-1.5"
                 style={{ background: "linear-gradient(to top, rgba(255,215,0,0.9), rgba(255,215,0,0.5) 60%, rgba(255,215,0,0))" }}
                 data-testid={`profile-current-title-${w.wrestler_id}`}>
              <img src={`/pvp/title_${w.current_title}.png`} alt={w.current_title} className="w-8 h-8 rounded-sm object-cover shrink-0" style={{ boxShadow: "0 0 10px #FFD700" }} />
              <div className="min-w-0 flex-1">
                <div className="text-[8px] font-heading uppercase tracking-widest text-black/70 leading-none">REIGNING</div>
                <div className="text-[10px] font-heading uppercase tracking-widest text-black leading-tight truncate">{labels[w.current_title]} CHAMP</div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="font-graffiti text-xl sm:text-2xl leading-tight truncate mb-2.5" style={{ color: "#fff", textShadow: `0 0 10px ${c}AA, 2px 2px 0 #000` }}>{w.name}</div>
      <div className="space-y-2.5 mb-2.5">
        <GearRow icon={Shirt} label="Gear" color="#00F0FF" items={[w.gear?.face, w.gear?.jacket, w.gear?.boots]} />
        <GearRow icon={UtensilsCrossed} label="Food Buffs" color="#FFE600" items={[w.food?.[0], w.food?.[1], w.food?.[2]]} />
      </div>
      <div data-testid={`profile-accomps-${w.wrestler_id}`}>
        <MiniHeading icon={Trophy} color="#FFD700">Accomplishments</MiniHeading>
        <div className="grid grid-cols-2 gap-1">
          <Accomp icon={Landmark} label="HOF" value={w.hof_points || 0} color="#B44DFF" />
          {divisions.map((div) => (
            <Accomp key={div} icon={Crown} label={short[div]} value={w.titles_won?.[div] || 0}
                    color={colors[div]} testId={`accomp-title-${div}-${w.wrestler_id}`} />
          ))}
          <Accomp icon={Trophy} label="RR" value={w.rumble_wins || 0} color="#FFE600" />
          <Accomp icon={Ticket} label="BANK" value={w.titleshot_bank_wins || 0} color="#00F0FF" />
          <Accomp icon={Dog} label="TD" value={w.top_dog_wins || 0} color="#FF007F" />
        </div>
      </div>
    </SlotShell>
  );
}
